import { memo, useState } from "react";
import { LuMinus, LuPlus, LuShoppingCart } from "react-icons/lu";
import { useCart } from "../hooks/useCart";

/**
 * "Add" for one pack; once the pack is in the cart it turns into a quantity
 * stepper. Errors (signed out, out of stock...) go to `onMessage`.
 */
function AddToCartButton({ priceId, productName, packLabel, size = "small", disabled, onMessage }) {
  const { add, setQuantity, lineFor } = useCart();
  const [pending, setPending] = useState(false);
  const line = priceId ? lineFor(priceId) : null;
  const large = size === "large";

  const run = async (action, done) => {
    setPending(true);
    try {
      await action();
      if (done) onMessage?.(done);
    } catch (error) {
      onMessage?.(error.message);
    } finally {
      setPending(false);
    }
  };

  if (line) {
    return (
      <div className={`ep-stepper ${large ? "is-large" : ""}`} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={`Remove one ${productName} ${packLabel}`}
          disabled={pending}
          onClick={() => run(() => setQuantity(line.id, line.quantity - 1))}
        >
          <LuMinus />
        </button>
        <output aria-live="polite" aria-label={`${line.quantity} in cart`}>
          {line.quantity}
        </output>
        <button
          type="button"
          aria-label={`Add one more ${productName} ${packLabel}`}
          disabled={pending}
          onClick={() => run(() => setQuantity(line.id, line.quantity + 1))}
        >
          <LuPlus />
        </button>
      </div>
    );
  }

  let text = large ? "Add to Cart" : "Add";
  if (disabled) text = "Out of stock";
  else if (pending) text = "Adding…";

  return (
    <button
      type="button"
      className={`ep-add ${large ? "is-large" : ""}`}
      aria-label={`Add ${productName}${packLabel ? ` ${packLabel}` : ""} to cart`}
      disabled={disabled || pending || !priceId}
      onClick={(e) => {
        e.stopPropagation();
        run(() => add(priceId), `${productName} added to cart`);
      }}
    >
      {!disabled && !pending && <LuShoppingCart aria-hidden="true" />}
      {text}
    </button>
  );
}

export default memo(AddToCartButton);
